const SEARCH_RENDER_LIMIT = 80;

function findVal(obj, fields) {
  if (!obj) return '';
  for (const field of fields) {
    const cleanField = String(field).toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const key in obj) {
      const cleanKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
      if (cleanKey === cleanField || cleanKey.includes(cleanField)) return String(obj[key]);
    }
  }
  return '';
}

export function createCatalogSearchUi({ formatCaseBrandTitle, hasActiveCase } = {}) {
  const searchTextCache = new WeakMap();
  const labelCache = new WeakMap();

  function normalizedSearchText(item) {
    if (!item || typeof item !== 'object') return String(item ?? '').toLowerCase();
    const cached = searchTextCache.get(item);
    if (cached !== undefined) return cached;
    const values = [];
    for (const value of Object.values(item)) {
      if (value === null || value === undefined) continue;
      if (typeof value === 'object') {
        for (const nested of Object.values(value)) {
          if (nested !== null && nested !== undefined && typeof nested !== 'object') values.push(String(nested));
        }
      } else {
        values.push(String(value));
      }
    }
    const text = values.join(' ').toLowerCase();
    searchTextCache.set(item, text);
    return text;
  }

  function buildUnifiedHardwareLabel(item) {
    if (item?.displayLabel) return String(item.displayLabel).trim();
    if (item && typeof item === 'object' && labelCache.has(item)) return labelCache.get(item);
    const brand = findVal(item, ['brand']).trim();
    const model = (findVal(item, ['model']) || findVal(item, ['cooler']) || findVal(item, ['aio']) || findVal(item, ['gpu'])).trim();
    const name = findVal(item, ['name', 'variant']).trim();

    const parts = [];
    if (brand) parts.push(brand);
    if (model) {
      if (brand && model.toLowerCase().startsWith(brand.toLowerCase())) parts.push(model.substring(brand.length).trim());
      else parts.push(model);
    }
    if (name) {
      const compiled = parts.join(' ').toLowerCase();
      if (!compiled.includes(name.toLowerCase())) parts.push(name);
    }
    const label = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (item && typeof item === 'object') labelCache.set(item, label);
    return label;
  }

  function initDynamicSearchCombobox(containerId, getDatasetFn, onSelectCallback, eligibilityFn = null) {
    const container = document.getElementById(containerId);
    if (!container) return;
    const input = container.querySelector('.combobox-input');
    const dropdown = container.querySelector('.combobox-dropdown');
    if (!input || !dropdown) return;

    function rebuildFilteredDropdownOptions(filterString = '') {
      const dataset = getDatasetFn?.() || [];
      const queryTokens = String(filterString || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
      const matchedData = [];

      for (const item of dataset) {
        if (queryTokens.length) {
          const matchString = normalizedSearchText(item);
          if (!queryTokens.every(token => matchString.includes(token))) continue;
        }
        if (eligibilityFn && !eligibilityFn(item)) continue;
        matchedData.push(item);
        if (matchedData.length >= SEARCH_RENDER_LIMIT) break;
      }

      if (!matchedData.length) {
        const emptyRow = document.createElement('div');
        emptyRow.className = 'combobox-item';
        emptyRow.style.color = 'var(--sub-text)';
        emptyRow.style.fontStyle = 'italic';
        emptyRow.textContent = (eligibilityFn && hasActiveCase?.()) ? 'No compatible records matched' : 'No records matched';
        dropdown.replaceChildren(emptyRow);
        return;
      }

      const fragment = document.createDocumentFragment();
      matchedData.forEach(row => {
        const rowElement = document.createElement('div');
        rowElement.className = 'combobox-item';
        let stringLabel = row.isCase ? formatCaseBrandTitle(row.name) : buildUnifiedHardwareLabel(row);
        if (row.volume) stringLabel += ` [${row.volume}L]`;
        else if (row.dimensionLabel) stringLabel += ` (${row.dimensionLabel})`;
        else if (row.l && row.w && row.h) stringLabel += ` (${row.l}x${row.w}x${row.h}mm)`;
        rowElement.textContent = stringLabel;
        rowElement.addEventListener('click', event => {
          event.stopPropagation();
          input.value = '';
          dropdown.classList.remove('active');
          onSelectCallback?.(row, stringLabel);
        });
        fragment.appendChild(rowElement);
      });
      dropdown.replaceChildren(fragment);
    }

    input.addEventListener('focus', () => {
      rebuildFilteredDropdownOptions(input.value);
      dropdown.classList.add('active');
    });
    input.addEventListener('input', () => {
      rebuildFilteredDropdownOptions(input.value);
      dropdown.classList.add('active');
    });
    document.addEventListener('click', event => {
      if (!container.contains(event.target)) dropdown.classList.remove('active');
    });
  }

  return { buildUnifiedHardwareLabel, initDynamicSearchCombobox };
}

export function filterCaseDatasetByVolume(dataset = []) {
  const minRaw = document.getElementById('case-volume-min')?.value;
  const maxRaw = document.getElementById('case-volume-max')?.value;
  const minVol = minRaw === '' || minRaw == null ? null : Number(minRaw);
  const maxVol = maxRaw === '' || maxRaw == null ? null : Number(maxRaw);
  return dataset.filter(row => {
    const volume = Number(row?.volume) || 0;
    if (Number.isFinite(minVol) && volume < minVol) return false;
    if (Number.isFinite(maxVol) && volume > maxVol) return false;
    return true;
  });
}

export function bindCaseVolumeSearchFilters() {
  const searchInput = document.querySelector('#caseSearchCombobox .combobox-input');
  ['case-volume-min','case-volume-max'].forEach(id => {
    const el=document.getElementById(id);
    if (!el || !searchInput) return;
    el.addEventListener('input', () => searchInput.dispatchEvent(new Event('input', { bubbles:true })));
  });
}

export async function displayLastSyncTime(meta = null) {
  try {
    let resolvedMeta=meta;
    if(!resolvedMeta){
      const response = await fetch('/meta.json', { cache:'no-store' });
      if (!response.ok) throw new Error('Metadata configuration file inaccessible');
      resolvedMeta = await response.json();
    }
    if(!resolvedMeta?.lastSynced)throw new Error('Metadata lastSynced value unavailable');
    const syncDate = new Date(resolvedMeta.lastSynced);
    const formattedTime = syncDate.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    const statusEl = document.getElementById('sync-status');
    if (statusEl) statusEl.textContent = `Data Last Synced: ${formattedTime}`;
  } catch (error) {
    console.warn('Sync metadata log unavailable:', error);
    const statusEl = document.getElementById('sync-status');
    if (statusEl) statusEl.textContent = '';
  }
}
